import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, Button, Typography } from '@mui/material';
import { GOOGLE_SSO_CLIENT_ID, IS_GOOGLE_SSO_CONFIGURED } from '../../config/apiConfig';
import { StudioFlowColors } from '../studioFlowTheme';
import type { GoogleCredentialResponse } from '../../types/google-photos';

const GOOGLE_SIGN_IN_TIMEOUT_MS = 60_000;

interface GoogleSsoContextValue {
  isConfigured: boolean;
  signInWithGoogle: () => Promise<string>;
}

const GoogleSsoContext = createContext<GoogleSsoContextValue>({
  isConfigured: false,
  signInWithGoogle: async () => {
    throw new Error('Google sign-in is not configured');
  },
});

function getGoogleIdentityServices() {
  return window.google?.accounts?.id;
}

function GoogleSsoBridge({ children }: { children: React.ReactNode }) {
  const resolverRef = useRef<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  } | null>(null);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);
  const initializedRef = useRef(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const clearSignInTimeout = useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const finishWithError = useCallback((message: string) => {
    clearSignInTimeout();
    const pending = resolverRef.current;
    resolverRef.current = null;
    setOverlayOpen(false);
    pending?.reject(new Error(message));
  }, [clearSignInTimeout]);

  const finishWithSuccess = useCallback((token: string) => {
    clearSignInTimeout();
    const pending = resolverRef.current;
    resolverRef.current = null;
    setOverlayOpen(false);
    pending?.resolve(token);
  }, [clearSignInTimeout]);

  useEffect(() => {
    const googleId = getGoogleIdentityServices();
    if (!googleId || initializedRef.current) {
      return undefined;
    }

    googleId.initialize({
      client_id: GOOGLE_SSO_CLIENT_ID,
      callback: (response: GoogleCredentialResponse) => {
        if (response.credential) {
          finishWithSuccess(response.credential);
          return;
        }

        finishWithError('Google sign-in did not return a credential');
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      ux_mode: 'popup',
    });

    initializedRef.current = true;
    return undefined;
  }, [finishWithError, finishWithSuccess]);

  useEffect(() => {
    if (!overlayOpen || !buttonContainerRef.current) {
      return undefined;
    }

    const googleId = getGoogleIdentityServices();
    const container = buttonContainerRef.current;

    if (!googleId) {
      finishWithError('Google sign-in is unavailable. Please try again.');
      return undefined;
    }

    container.replaceChildren();
    googleId.renderButton(container, {
      type: 'standard',
      size: 'large',
      theme: 'outline',
      text: 'signin_with',
      width: 300,
    });

    timeoutRef.current = window.setTimeout(() => {
      finishWithError('Google sign-in timed out. Please try again.');
    }, GOOGLE_SIGN_IN_TIMEOUT_MS);

    return () => {
      clearSignInTimeout();
    };
  }, [overlayOpen, clearSignInTimeout, finishWithError]);

  const handleCancel = useCallback(() => {
    finishWithError('Google sign-in was cancelled.');
  }, [finishWithError]);

  const signInWithGoogle = useCallback(() => new Promise<string>((resolve, reject) => {
    const googleId = getGoogleIdentityServices();
    if (!googleId || !initializedRef.current) {
      reject(new Error('Google sign-in is still loading. Please try again.'));
      return;
    }

    if (resolverRef.current) {
      reject(new Error('Google sign-in is already in progress.'));
      return;
    }

    resolverRef.current = { resolve, reject };
    setOverlayOpen(true);
  }), []);

  return (
    <GoogleSsoContext.Provider
      value={{ isConfigured: true, signInWithGoogle }}
    >
      {children}
      {overlayOpen ? (
        <Box
          role="presentation"
          onClick={handleCancel}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            bgcolor: 'rgba(0, 8, 20, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Box
            role="dialog"
            aria-labelledby="google-signin-title"
            onClick={(event) => event.stopPropagation()}
            sx={{
              bgcolor: StudioFlowColors.background,
              borderRadius: '16px',
              boxShadow: '0 12px 40px rgba(0, 8, 20, 0.18)',
              p: 3,
              width: '100%',
              maxWidth: 360,
              textAlign: 'center',
            }}
          >
            <Typography
              id="google-signin-title"
              sx={{
                color: StudioFlowColors.title,
                fontWeight: 700,
                fontSize: 18,
                mb: 1,
              }}
            >
              Continue with Google
            </Typography>
            <Typography
              sx={{
                color: StudioFlowColors.subtitle,
                fontSize: 13,
                mb: 3,
              }}
            >
              Choose your Google account to sign in to Chatbooks Studio.
            </Typography>
            <Box
              ref={buttonContainerRef}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                minHeight: 44,
                mb: 2,
              }}
            />
            <Button
              variant="text"
              onClick={handleCancel}
              sx={{ color: StudioFlowColors.subtitle, textTransform: 'none' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      ) : null}
    </GoogleSsoContext.Provider>
  );
}

export const GoogleSsoProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  if (!IS_GOOGLE_SSO_CONFIGURED) {
    return <>{children}</>;
  }

  return <GoogleSsoBridge>{children}</GoogleSsoBridge>;
};

export function useGoogleSso(): GoogleSsoContextValue {
  return useContext(GoogleSsoContext);
}
